// AGC: VoiceRecordingModal - Full-screen modal for voice recording
// AGC: Shows recording progress, processing state, and results
// AGC: Updated for hybrid voice system with better error handling
// AGC: Now includes field suggestions popup for unrecognized values
// AGC: Delegates confirmFields rendering to VoiceWizardConfirmation / VoiceSinglePageConfirmation

import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Mic, X, CheckCircle, AlertCircle, Loader2, Cloud, Smartphone, Wifi, WifiOff, RefreshCw, ArrowRight, Square, Check, Plus } from 'lucide-react'
import { getFinancialTreatment, type StockTreatmentMode } from '../../utils/financialTreatmentPolicy'
import toast from 'react-hot-toast'
import { useTranslation } from '../../hooks/useTranslation'
import { useFarms } from '../../context'
// AGC: v3.9.4 — PREMIUM agent mode imports
import { useCapability } from '../../hooks/useCapability'
import { useAuthStore } from '../../services/authStore'
import { Capacitor } from '@capacitor/core'
import { TextToSpeech } from '@capacitor-community/text-to-speech'
import { getBackendUrl } from '../../utils/backendUrl'
import { PermissionBlockedModal } from '../common/PermissionBlockedModal'
import { isMediaPermissionError } from '../../services/permissionHelper'
import type { RecordingState, VoiceProcessingResponse, FarmSelectionInfo } from '../../hooks/useVoiceRecording'
import { VoiceWizardConfirmation } from './VoiceWizardConfirmation'
import { VoiceSinglePageConfirmation } from './VoiceSinglePageConfirmation'
import { VoiceAgentConfirm } from './VoiceAgentConfirm'
import { sendToAgentV2, confirmAgentAction, sendTextToAgentV2, type VoiceAgentV2Response } from '../../services/voiceAgentV2'
// AGC: Analytics telemetry for voice flow — retry + parse_failed paths
import { VoiceTelemetry } from '../../lib/voice-telemetry'

interface VoiceRecordingModalProps {
  /** AGC: Whether modal is visible */
  isVisible: boolean
  /** AGC: Current recording state */
  state: RecordingState
  /** AGC: Recording progress (0-100) */
  progress: number
  /** AGC: Recording duration in seconds */
  duration: number
  /** AGC: Error message if any */
  error: string | null
  /** AGC: Last processing response */
  lastResponse: VoiceProcessingResponse | null
  /** AGC: Callback to close modal */
  onClose: () => void
  /** AGC: Callback to stop recording */
  onStopRecording: () => void
  /** AGC: Callback to cancel recording */
  onCancelRecording: () => void
  /** AGC: Silent cancel — stop hook's recorder without closing modal (v2 turn handoff) */
  onSilentCancel?: () => void
  /** AGC: Voice mode (A = Whisper/Paid, B = Native/Free) - optional for hybrid system */
  voiceMode?: 'A' | 'B'
  /** AGC: Whether device is online - optional for hybrid system */
  isOnline?: boolean
  /** AGC: Number of commands in offline queue - optional for hybrid system */
  queueCount?: number
  /** AGC: Callback when user wants to create entry manually */
  onManualEntry?: (entryType: string, transcription: string) => void
  /** AGC: Callback to retry recording */
  onRetry?: () => void
  /** AGC: Callback when user selects a suggestion for an unrecognized field */
  onFieldSuggestionSelect?: (fieldName: string, selectedValue: string, label: string) => void
  /** AGC: Callback when user clicks Confirm with all filled values (merged parsedData + inputValues) */
  onConfirmFields?: (mergedData: Record<string, string | number | null>) => void
  /** AGC: Callback when user selects a farm for lot creation (multi-farm scenario) */
  onFarmSelect?: (farmId: string, farmName: string, pendingLotData: Record<string, unknown>) => void
  /** AGC: Callback when user wants to create a new farm from the voice modal */
  onCreateFarm?: (farmName: string, pendingLotData: Record<string, unknown>) => void
  /** AGC: Skip recording UI, start at processing state (used with inline recording bar) */
  processingOnly?: boolean
  /** AGC: Pre-captured audio blob from recording bar — modal sends it to v2 agent on mount */
  pendingBlob?: Blob | null
}

/**
 * AGC: Entry types for manual selection
 */
// AGC: Entry type IDs — labels come from i18n at render time
const ENTRY_TYPE_IDS = [
  { id: 'farm', labelKey: 'voice.entryTypes.createFarm', icon: '🏠' },
  { id: 'field', labelKey: 'voice.entryTypes.createField', icon: '🗺️' },
  { id: 'task', labelKey: 'voice.entryTypes.createTask', icon: '📋' },
  { id: 'expense', labelKey: 'voice.entryTypes.registerExpense', icon: '💰' },
  { id: 'revenue', labelKey: 'voice.entryTypes.registerRevenue', icon: '💵' },
  { id: 'inventory', labelKey: 'voice.entryTypes.registerInventory', icon: '📦' },
  { id: 'vehicle', labelKey: 'voice.entryTypes.registerVehicle', icon: '🚜' },
]

/**
 * AGC: VoiceRecordingModal - Modal for voice recording interface
 * AGC: Displays circular progress ring with microphone icon
 * AGC: Shows clear error messages and manual entry options when command not recognized
 */
export const VoiceRecordingModal: React.FC<VoiceRecordingModalProps> = ({
  isVisible,
  state,
  progress,
  duration,
  error,
  lastResponse,
  onClose,
  onStopRecording,
  onCancelRecording,
  onSilentCancel,
  voiceMode = 'B', // AGC: Default to Native/Free mode
  isOnline = true, // AGC: Default to online
  queueCount = 0, // AGC: Default to no queued commands
  onManualEntry,
  onRetry,
  onFieldSuggestionSelect,
  onConfirmFields,
  onFarmSelect,
  onCreateFarm,
  processingOnly = false,
  pendingBlob = null,
}) => {
  const { t, language } = useTranslation()
  const { farms, currentFarm } = useFarms()
  // AGC: v3.9.4 — PREMIUM agent mode capability
  const voiceAgentEnabled = useCapability('voiceAgentEnabled')
  const voiceAgentDefault = useCapability('voiceAgentDefaultMode') as 'A' | 'B'
  const getAuthToken = useAuthStore((s) => s.getAuthToken)

  // AGC: Show manual entry options when command not recognized
  const [showManualOptions, setShowManualOptions] = useState(false)
  // AGC: Track text input values for each field
  const [inputValues, setInputValues] = useState<Record<string, string>>({})
  // AGC: Pre-modal phase - shows instructions + mic before the confirmation form
  // AGC: 'preModal' = showing instructions with mic, 'confirmFields' = showing the form, null = not in field flow
  const [fieldPhase, setFieldPhase] = useState<'preModal' | 'confirmFields' | null>(null)
  // AGC: Farm selection state for multi-farm scenario
  const [selectedFarmId, setSelectedFarmId] = useState<string | null>(null)
  // AGC: New farm creation state
  const [showNewFarmInput, setShowNewFarmInput] = useState(false)
  const [newFarmName, setNewFarmName] = useState('')
  // AGC: Pre-modal recording state
  const [preModalRecording, setPreModalRecording] = useState(false)
  // AGC: Pre-modal processing state (sending to extract-fields endpoint)
  const [preModalProcessing, setPreModalProcessing] = useState(false)
  // AGC: Pre-modal transcription result
  const [preModalTranscription, setPreModalTranscription] = useState<string | null>(null)
  // AGC: MediaRecorder ref for pre-modal recording
  const preModalRecorderRef = useRef<MediaRecorder | null>(null)
  // AGC: Audio chunks for pre-modal recording
  const preModalChunksRef = useRef<Blob[]>([])
  // AGC: Pre-modal recording duration
  const [preModalDuration, setPreModalDuration] = useState(0)
  // AGC: Pre-modal timer ref
  const preModalTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // AGC: Microphone permission blocked modal
  const [showMicBlocked, setShowMicBlocked] = useState(false)
  // AGC: Stock toggle for "ask" categories (fuel, machinery_parts, custom ask)
  const [stockToggle, setStockToggle] = useState(true)
  // AGC: Custom financial categories — persisted to localStorage, managed via CategoryField
  const [customFinancialCategories, setCustomFinancialCategories] = useState<Array<{ id: string; label: string; stockTreatment?: StockTreatmentMode }>>([])

  // AGC: Confirmation mode — wizard (step-by-step) or single (1-page form)
  const [confirmMode, setConfirmMode] = useState<'wizard' | 'single'>(() => {
    try {
      return (localStorage.getItem('voice_confirm_mode') as 'wizard' | 'single') || 'wizard'
    } catch { return 'wizard' }
  })

  // AGC: Persist confirmation mode preference to localStorage
  const handleModeSwitch = useCallback((mode: 'wizard' | 'single') => {
    setConfirmMode(mode)
    try { localStorage.setItem('voice_confirm_mode', mode) } catch { /* noop */ }
  }, [])

  // AGC: v3.9.4 — PREMIUM agent mode state
  // AGC: v3.9.23 — Chat/Intent toggle visible again, but Chat is locked ("Em breve") while the
  // AGC: conversational agent UX is being polished. Command (Intent) is the default and the only
  // AGC: functional mode. Flip CHAT_MODE_FUNCTIONAL=true to allow tapping Chat.
  const CHAT_MODE_FUNCTIONAL = false
  // AGC: v3.9.24 — Voice Agent v2 (confirmation-box path via farm_app/). Flip to true per release.
  const AGENT_V2_ENABLED = true
  const [v2Response, setV2Response] = useState<VoiceAgentV2Response | null>(null)
  const [v2Busy, setV2Busy] = useState(false)
  const [useAgent, setUseAgent] = useState<boolean>(() => AGENT_V2_ENABLED)
  const [agentMode, setAgentMode] = useState<'A' | 'B'>(() => {
    const stored = localStorage.getItem('voiceAgentMode')
    return (stored === 'A' || stored === 'B') ? stored : voiceAgentDefault
  })
  const [transcript, setTranscript] = useState<Array<{ role: 'user' | 'agent'; text: string }>>([])
  // AGC: v3.9.19 — agent flow phase: 'idle' | 'recording' | 'processing' | 'speaking'.
  // AGC: Drives the modal UI so it never looks "closed" between turns (state stays 'idle' on the
  // AGC: hook side during the whole agent round-trip; without this the modal goes blank).
  const [agentPhase, setAgentPhase] = useState<'idle' | 'recording' | 'processing' | 'speaking'>('idle')
  // AGC: isAgentSpeaking tracks TTS activity — drives mic auto-reopen in Mode A
  const isAgentSpeakingRef = useRef(false)
  const sessionIdRef = useRef<string | null>(null)
  // AGC: v3.9.16 — TTS voice cache refs MUST be declared above the `if (!isVisible) return null`
  // AGC: early return at line ~883. Declaring them after the early return caused React error #310
  // AGC: ("rendered more hooks than previous render") the moment the modal first became visible.
  const preferredVoiceIdxRef = useRef<number | null>(null)
  const voiceLookupDoneRef = useRef(false)
  // AGC: v3.9.41 — Persistent mic stream for v2. Kept alive across recordings in the same modal
  // AGC: session so the user can hit "Corrigir" and re-record without Android WebView losing the
  // AGC: mic. Released when the modal closes.
  const v2StreamRef = useRef<MediaStream | null>(null)

  // AGC: Persist toggles device-local (user preference, not tier metadata)
  useEffect(() => { localStorage.setItem('voiceAgentEnabled', useAgent ? '1' : '0') }, [useAgent])
  useEffect(() => { localStorage.setItem('voiceAgentMode', agentMode) }, [agentMode])

  // AGC: v3.9.5 — If tier downgrades mid-session and voiceAgentEnabled flips to false,
  // AGC: forcibly disable Chat mode so the user can't keep firing PREMIUM-only requests.
  useEffect(() => { if (!voiceAgentEnabled && useAgent) setUseAgent(false) }, [voiceAgentEnabled, useAgent])

  // AGC: v3.9.4 — Intercept stop-recording in agent mode: stop local recorder and send to agent-chat
  const handleStopRecording = async () => {
    console.log(`🛑 AGC: handleStopRecording fired — useAgent=${useAgent} voiceAgentEnabled=${voiceAgentEnabled} AGENT_V2=${AGENT_V2_ENABLED} preModalRef=${!!preModalRecorderRef.current} preModalState=${preModalRecorderRef.current?.state}`)
    // AGC: v3.9.41 — When v2 is enabled, always route through Farm Agent (entitlement checked server-side).
    if (AGENT_V2_ENABLED) {
      if (!preModalRecorderRef.current || preModalRecorderRef.current.state === 'inactive') {
        if (state === 'recording' || preModalRecording) {
          startPreModalRecording()
          return
        }
        return
      }
      setAgentPhase('processing')
      setV2Busy(true)
      if (preModalTimerRef.current) {
        clearInterval(preModalTimerRef.current)
        preModalTimerRef.current = null
      }
      const recorder = preModalRecorderRef.current
      try { recorder.requestData() } catch { /* not all browsers support requestData */ }
      await new Promise<void>((resolve) => {
        recorder.onstop = async () => {
          const blob = new Blob(preModalChunksRef.current, { type: recorder.mimeType || 'audio/webm' })
          console.log(`AGC: [voice-v2] blob size=${blob.size} chunks=${preModalChunksRef.current.length}`)
          try {
            const resp = await sendToAgentV2(blob, language, { reset: false })
            sessionIdRef.current = resp.sessionId
            setV2Response(resp)
          } catch (err) {
            console.error('[voice-v2] send error:', err)
            toast.error(t('voice.agent.errorGeneric'))
          } finally {
            setV2Busy(false)
            setAgentPhase('idle')
            resolve()
          }
        }
        recorder.stop()
        // AGC: v3.9.43 — Keep v2StreamRef ALIVE so Corrigir can reuse it without a new
        // AGC: getUserMedia (Android WebView gives a muted track on the second call).
        // AGC: Only release hook's recorder — it auto-stops at 60s and hogs the mic.
        onSilentCancel?.()
      })
      setPreModalRecording(false)
      preModalChunksRef.current = []
      preModalRecorderRef.current = null
      // AGC: v3.9.41 — Do NOT call onCancelRecording here. The modal stays open to show
      // AGC: the VoiceAgentConfirm UI. The user dismisses via confirm/edit/close buttons.
      return
    }
    // AGC: v1 (Plan B) — Chat agent path, gated on premium + agent toggle
    if (useAgent && voiceAgentEnabled) {
      setAgentPhase('processing')
      if (preModalRecorderRef.current && preModalRecorderRef.current.state !== 'inactive') {
        if (preModalTimerRef.current) {
          clearInterval(preModalTimerRef.current)
          preModalTimerRef.current = null
        }
        const recorder = preModalRecorderRef.current
        await new Promise<void>((resolve) => {
          recorder.onstop = async () => {
            const recorderMime = recorder.mimeType || 'audio/webm'
            const blob = new Blob(preModalChunksRef.current, { type: recorderMime })
            setTranscript(prev => [...prev, { role: 'user', text: '🎤 …' }])
            await sendToAgentChat(blob)
            resolve()
          }
          recorder.stop()
          recorder.stream?.getTracks().forEach(track => track.stop())
        })
        setPreModalRecording(false)
        preModalChunksRef.current = []
        preModalRecorderRef.current = null
      }
      onCancelRecording()
      return
    }
    // AGC: Non-agent path — normal SOTA hook stop
    onStopRecording()
  }

  // AGC: v2 agent — dispatch page-refresh events when backend creates entries
  useEffect(() => {
    if (v2Response?.entryCreated) {
      window.dispatchEvent(new CustomEvent('refresh-machines'))
      window.dispatchEvent(new CustomEvent('refresh-finances'))
      window.dispatchEvent(new CustomEvent('refresh-inventory'))
      window.dispatchEvent(new CustomEvent('refresh-fields'))
      window.dispatchEvent(new CustomEvent('task-created'))
    }
  }, [v2Response])

  // AGC: Auto-close after successful processing
  // AGC: SKIP auto-close when fieldSuggestions exist or fieldPhase is active (preModal/confirmFields)
  // AGC: Those flows require user interaction before closing
  useEffect(() => {
    // AGC: v3.9.41 — Skip auto-close entirely when v2 is enabled. The VoiceAgentConfirm
    // AGC: component owns the dismiss flow via its own confirm/edit/close buttons.
    if (AGENT_V2_ENABLED) return
    const hasFieldSuggestions = lastResponse?.fieldSuggestions && Object.keys(lastResponse.fieldSuggestions).length > 0
    const hasFollowUp = !!lastResponse?.followUpQuestion
    if (lastResponse?.success && state === 'idle' && !hasFieldSuggestions && fieldPhase === null && !hasFollowUp) {
      const timer = setTimeout(() => {
        onClose()
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [lastResponse, state, onClose, fieldPhase, v2Response, v2Busy, agentPhase])

  // AGC: Show manual options when command not recognized
  // AGC: BUT NOT when we have field suggestions (those show their own UI)
  // AGC: When field suggestions arrive, start in preModal phase to let user dictate values
  useEffect(() => {
    const hasFieldSuggestions = lastResponse?.fieldSuggestions && 
      Object.keys(lastResponse.fieldSuggestions).length > 0
    
    if ((lastResponse?.commandNotRecognized || lastResponse?.suggestManualEntry) && !hasFieldSuggestions) {
      setShowManualOptions(true)
      // AGC: Telemetry — backend could not parse intent from transcript
      void VoiceTelemetry.fail('parse_failed')
    } else {
      setShowManualOptions(false)
    }
    
    // AGC: Pre-populate inputValues from fieldSuggestions values
    // AGC: This ensures that values extracted by the backend (e.g., quantity=1000 from "mil reais")
    // AGC: appear pre-filled in the confirmation form inputs instead of showing empty
    const prePopulated: Record<string, string> = {}
    if (lastResponse?.fieldSuggestions) {
      for (const [key, info] of Object.entries(lastResponse.fieldSuggestions)) {
        const fieldInfo = info as any
        // AGC: Handle machinery auto-selection — convert autoSelectedMachines array
        // AGC: to the selectedMachinery JSON format used by the machinery UI selector
        if (key === 'machinery' && fieldInfo.autoSelectedMachines?.length > 0) {
          prePopulated.selectedMachinery = JSON.stringify(fieldInfo.autoSelectedMachines)
        // AGC: Handle inputs auto-selection — convert autoSelectedInputs array
        } else if (key === 'inputs' && fieldInfo.autoSelectedInputs?.length > 0) {
          prePopulated.selectedInputs = JSON.stringify(fieldInfo.autoSelectedInputs)
        } else if (fieldInfo.value != null && fieldInfo.value !== '') {
          prePopulated[key] = String(fieldInfo.value)
        }
      }
    }
    // AGC: For tasks, ensure dueDate exists in fieldSuggestions and inputValues
    // AGC: The deployed backend may not include fieldSuggestions.dueDate — inject it client-side
    if (lastResponse?.entryType === 'task') {
      const todayISO = new Date().toISOString().split('T')[0]
      const tomorrowDate = new Date()
      tomorrowDate.setDate(tomorrowDate.getDate() + 1)
      const tomorrowISO = tomorrowDate.toISOString().split('T')[0]
      if (!prePopulated.dueDate) {
        prePopulated.dueDate = todayISO
      }
      // AGC: Inject dueDate into fieldSuggestions so the confirmation form renders it
      if (lastResponse.fieldSuggestions && !lastResponse.fieldSuggestions.dueDate) {
        lastResponse.fieldSuggestions.dueDate = {
          value: prePopulated.dueDate,
          confidence: 50,
          suggestions: [
            { value: todayISO, label: 'Hoje', confidence: 80 },
            { value: tomorrowISO, label: 'Amanhã', confidence: 70 },
          ],
          missing: false,
          fieldLabel: 'Data',
          isDueDateSelector: true,
          inputType: 'date',
        } as any
      }
    }
    setInputValues(prePopulated)
    setPreModalTranscription(null)
    
    // AGC: If we have field suggestions, show pre-modal first
    // AGC: EXCEPT for field creation in app flow - cropType and area are handled later:
    // AGC:   - area is calculated from map boundaries (GPS/Draw/AI)
    // AGC:   - cropType is selected in CropSuggestions screen
    // AGC: So we skip the preModal and auto-confirm to navigate directly to novo-lote
    if (hasFieldSuggestions) {
      if (lastResponse?.entryType === 'field') {
        const suggestionKeys = Object.keys(lastResponse.fieldSuggestions)
        // AGC: Fields that are optional for app flow (handled later in the creation process)
        // AGC: area = calculated from map/GPS/AI boundary design
        // AGC: cropType = selected in CropSuggestions screen
        // AGC: managementStatus = optional metadata, not needed for lot creation
        const appOptionalFields = ['cropType', 'area', 'managementStatus']
        const allOptionalForApp = suggestionKeys.every(k => appOptionalFields.includes(k))
        
        if (allOptionalForApp && onConfirmFields && lastResponse.parsedData) {
          // AGC: Skip preModal - auto-confirm and navigate directly to field creation
          console.log('🚀 AGC: Field voice command - skipping preModal (only optional fields missing)')
          console.log('📋 AGC: Auto-confirming with parsed data:', lastResponse.parsedData)
          setFieldPhase(null)
          // AGC: Use setTimeout to avoid calling during render cycle
          setTimeout(() => {
            onConfirmFields(lastResponse.parsedData!)
          }, 100)
          return
        }
      }
      // AGC: For tasks, always skip preModal and go straight to confirmFields
      // AGC: Tasks default dueDate to today and category to 'outros' — no voice dictation needed
      // AGC: The user can edit any field in the confirmation form before tapping Confirm
      if (lastResponse?.entryType === 'task') {
        console.log('⏭️ AGC: Task — skipping preModal, going to confirmFields with defaults')
        setFieldPhase('confirmFields')
      } else {
        // AGC: For non-task entries, check if there are truly missing fields that need voice dictation
        const hasTrulyMissingFields = Object.values(lastResponse.fieldSuggestions).some(
          (info: any) => info.missing || info._required
        )
        if (hasTrulyMissingFields) {
          setFieldPhase('preModal')
        } else {
          console.log('⏭️ AGC: All fields have values, skipping preModal → confirmFields')
          setFieldPhase('confirmFields')
        }
      }
    } else {
      setFieldPhase(null)
    }
  }, [lastResponse, onConfirmFields])

  // AGC: For field (lot) creation, default selected farm: single farm or last used
  useEffect(() => {
    if (
      lastResponse?.entryType === 'field' &&
      farms.length > 0 &&
      (selectedFarmId === null || !farms.some((f) => f.id === selectedFarmId))
    ) {
      const defaultId = farms.length === 1 ? farms[0].id : (currentFarm?.id ?? farms[0].id)
      setSelectedFarmId(defaultId)
    }
  }, [lastResponse?.entryType, farms, currentFarm?.id, selectedFarmId])

  // AGC: Reset show manual options and clean up pre-modal recording when modal closes
  useEffect(() => {
    if (!isVisible) {
      setShowManualOptions(false)
      setFieldPhase(null)
      setPreModalRecording(false)
      setPreModalProcessing(false)
      setPreModalTranscription(null)
      setPreModalDuration(0)
      // AGC: Reset farm selection state
      setSelectedFarmId(null)
      setShowNewFarmInput(false)
      setNewFarmName('')
      // AGC: Clean up pre-modal recording if active
      if (preModalTimerRef.current) {
        clearInterval(preModalTimerRef.current)
        preModalTimerRef.current = null
      }
      if (preModalRecorderRef.current && preModalRecorderRef.current.state !== 'inactive') {
        preModalRecorderRef.current.stream?.getTracks().forEach(track => track.stop())
        preModalRecorderRef.current.stop()
        preModalRecorderRef.current = null
      }
      // AGC: v3.9.41 — Release persistent v2 mic stream when modal closes.
      if (v2StreamRef.current) {
        v2StreamRef.current.getTracks().forEach(t => t.stop())
        v2StreamRef.current = null
      }
      setV2Response(null)
      setV2Busy(false)
    }
  }, [isVisible])

  // AGC: Load custom financial categories from localStorage (synced with Sistema > Categorias)
  const customCatStorageKey = `droidfarm_custom_categories_${currentFarm?.id || 'default'}`
  useEffect(() => {
    try {
      const raw = localStorage.getItem(customCatStorageKey)
      if (raw) {
        const parsed = JSON.parse(raw) as Array<{ id: string; label: string; stockTreatment?: StockTreatmentMode }>
        if (Array.isArray(parsed)) setCustomFinancialCategories(parsed)
      }
    } catch { /* AGC: Ignore corrupt data */ }
  }, [customCatStorageKey])

  // AGC: Reset stockToggle to policy default when category changes
  const currentCat = inputValues.category || (lastResponse?.parsedData?.category as string) || ''
  useEffect(() => {
    if (!currentCat || !lastResponse?.entryType) return
    const et = lastResponse.entryType as 'expense' | 'revenue'
    if (et !== 'expense' && et !== 'revenue') return
    const customCat = customFinancialCategories.find(c => c.id === currentCat)
    const base = getFinancialTreatment({
      entryType: et,
      category: currentCat,
      customStockTreatment: customCat?.stockTreatment || null,
      itemQuantity: lastResponse?.parsedData?.itemQuantity as number | null,
      itemUnit: lastResponse?.parsedData?.itemUnit as string | null,
      title: (lastResponse?.parsedData?.title as string | null) || lastResponse?.transcription || null,
      userOverride: null,
    })
    if (base.showStockToggle) {
      setStockToggle(base.defaultStockToggleValue)
    }
  }, [currentCat, lastResponse?.entryType, lastResponse?.parsedData?.itemQuantity, lastResponse?.parsedData?.itemUnit, lastResponse?.transcription, customFinancialCategories])

  // AGC: Save a new custom category — same localStorage key as Sistema > Categorias
  // AGC: Accepts label string from CategoryField's onAddCustomCategory prop
  const handleAddCustomCategory = useCallback((label: string) => {
    const trimmed = label.trim()
    if (!trimmed) return
    const base = trimmed.toLowerCase().replace(/[^a-z0-9áéíóúàãõâêôçñü\s]/gi, '').replace(/\s+/g, '_')
    const allIds = new Set(customFinancialCategories.map(c => c.id))
    let id = base
    let n = 0
    while (allIds.has(id)) { n++; id = `${base}_${n}` }
    const updated = [...customFinancialCategories, { id, label: trimmed, stockTreatment: 'expense' as const }]
    setCustomFinancialCategories(updated)
    try { localStorage.setItem(customCatStorageKey, JSON.stringify(updated)) } catch { /* ignore */ }
    setInputValues(prev => ({ ...prev, category: id }))
  }, [customFinancialCategories, customCatStorageKey])

  // AGC: Calculate SVG circle properties for progress ring
  const size = 200
  const strokeWidth = 8
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (progress / 100) * circumference

  // AGC: Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // AGC: Get status message based on state — uses i18n for all strings
  const getStatusMessage = (): string => {
    switch (state) {
      case 'requesting':
        return t('voice.requestingMicPermission')
      case 'recording':
        return t('voice.recordingRelease')
      case 'processing':
        return t('voice.processingMessage')
      case 'error':
        return error || t('voice.processingError')
      case 'idle':
        if (lastResponse?.success) {
          return lastResponse.message || t('voice.commandSuccess')
        }
        if (lastResponse?.fieldSuggestions && Object.keys(lastResponse.fieldSuggestions).length > 0) {
          return lastResponse.message || t('voice.completeFields')
        }
        if (lastResponse?.commandNotRecognized) {
          return t('voice.commandNotRecognizedMessage')
        }
        if (lastResponse?.lowConfidence) {
          return t('voice.commandNotUnderstood')
        }
        if (lastResponse && !lastResponse.success) {
          return lastResponse.message || t('voice.commandProcessingError')
        }
        return t('voice.readyToRecord')
      default:
        return ''
    }
  }

  // AGC: Get secondary message for error states — uses i18n
  const getSecondaryMessage = (): string | null => {
    if (state === 'idle') {
      if (lastResponse?.commandNotRecognized || lastResponse?.suggestManualEntry) {
        return t('voice.chooseOptionOrRetry')
      }
      if (lastResponse?.suggestRetry) {
        return t('voice.speakMoreClearly')
      }
    }
    return null
  }

  // AGC: Get icon based on state
  const renderIcon = () => {
    switch (state) {
      case 'processing':
        return (
          <Loader2 
            className="w-12 h-12 text-white animate-spin" 
            data-testid="processing-icon"
          />
        )
      case 'error':
        return (
          <AlertCircle 
            className="w-12 h-12 text-red-400" 
            data-testid="error-icon"
          />
        )
      case 'idle':
        if (lastResponse?.success) {
          return (
            <CheckCircle 
              className="w-12 h-12 text-green-400" 
              data-testid="success-icon"
            />
          )
        }
        // AGC: Show warning for field suggestions or unrecognized commands
        if (lastResponse?.fieldSuggestions && Object.keys(lastResponse.fieldSuggestions).length > 0) {
          return (
            <AlertCircle 
              className="w-12 h-12 text-yellow-400" 
              data-testid="warning-icon"
            />
          )
        }
        if (lastResponse?.commandNotRecognized || lastResponse?.lowConfidence) {
          return (
            <AlertCircle 
              className="w-12 h-12 text-yellow-400" 
              data-testid="warning-icon"
            />
          )
        }
        return (
          <Mic 
            className="w-12 h-12 text-white" 
            data-testid="microphone-icon"
          />
        )
      default:
        return (
          <Mic 
            className={`w-12 h-12 text-white ${state === 'recording' ? 'animate-pulse' : ''}`}
            data-testid="microphone-icon"
          />
        )
    }
  }

  // AGC: Get ring color based on state
  const getRingColor = (): string => {
    switch (state) {
      case 'error':
        return '#EF4444' // AGC: Red
      case 'idle':
        if (lastResponse?.success) {
          return '#10B981' // AGC: Green
        }
        // AGC: Yellow for field suggestions or unrecognized commands
        if (lastResponse?.fieldSuggestions && Object.keys(lastResponse.fieldSuggestions).length > 0) {
          return '#F59E0B' // AGC: Yellow/warning
        }
        if (lastResponse?.commandNotRecognized || lastResponse?.lowConfidence) {
          return '#F59E0B' // AGC: Yellow/warning
        }
        return '#10B981'
      default:
        return '#10B981' // AGC: Green
    }
  }

  // AGC: Handle manual entry selection
  const handleManualEntrySelect = (entryType: string) => {
    if (onManualEntry && lastResponse?.transcription) {
      onManualEntry(entryType, lastResponse.transcription)
    }
    onClose()
  }

  // AGC: Handle retry
  const handleRetry = () => {
    setShowManualOptions(false)
    // AGC: Telemetry — user rejected parse result and chose to re-record
    VoiceTelemetry.retry()
    if (onRetry) {
      onRetry()
    }
    onClose()
  }

  /**
   * AGC: Get the list of missing/required fields from fieldSuggestions
   * AGC: Used by the pre-modal to show the user what fields they need to provide
   */
  const getMissingFields = useCallback(() => {
    if (!lastResponse?.fieldSuggestions) return []
    // AGC: Check if brand was already detected (exists in parsedData)
    const brandAlreadyDetected = !!lastResponse?.parsedData?.brand
    // AGC: Check if title was already detected and is not a generic default
    const titleAlreadyDetected = !!lastResponse?.parsedData?.title && 
      !['tarefa', 'task', 'tarea', ''].includes((lastResponse?.parsedData?.title || '').toLowerCase().trim())
    // AGC: Check if category was already detected and is not the default 'outros'
    const categoryAlreadyDetected = !!lastResponse?.parsedData?.category && 
      lastResponse?.parsedData?.category !== 'outros' && 
      lastResponse?.parsedData?.category !== ''
    // AGC: Check if dueDate was already detected (any non-empty ISO date string)
    const dueDateAlreadyDetected = !!lastResponse?.parsedData?.dueDate && 
      lastResponse?.parsedData?.dueDate !== ''
    return Object.entries(lastResponse.fieldSuggestions)
      .filter(([fieldName, info]) => {
        // AGC: Skip internal/system slots that shouldn't be shown to user
        if (fieldName === 'intentType' || fieldName === 'intentStrength' || fieldName === 'constraints' || fieldName === 'operation') return false
        // AGC: Skip brand from "missing" list if it was already detected by GPT/fuzzy matching
        // AGC: The user can still change it in the confirmation form (brand selector)
        if (fieldName === 'brand' && info.isBrandSelector && brandAlreadyDetected) return false
        // AGC: Skip machinery from pre-modal voice prompt - must be selected from UI dropdown
        if (fieldName === 'machinery' && info.isMachinerySelector) return false
        // AGC: Skip inputs/products from voice - interactive UI selector needed
        if (fieldName === 'inputs' && info.isInputSelector) return false
        // AGC: Skip category if already detected - can be changed in grid selector
        if (fieldName === 'category' && info.isCategorySelector && categoryAlreadyDetected) return false
        // AGC: Skip priority from voice prompt - 3-button selector in UI form is better
        if (fieldName === 'priority' && info.isPrioritySelector) return false
        // AGC: Skip title if already detected with a real name
        if (fieldName === 'title' && info.isTitleInput && titleAlreadyDetected) return false
        // AGC: Skip dueDate if already detected - shown as editable in confirmation form
        if (fieldName === 'dueDate' && dueDateAlreadyDetected) return false
        return info.missing || info.isBrandSelector || info._required
      })
      .map(([fieldName, info]) => ({
        name: fieldName,
        label: info.fieldLabel || fieldName,
        type: info.inputType || 'text',
        isBrand: !!info.isBrandSelector,
      }))
  }, [lastResponse])

  /**
   * AGC: Get a human-readable example phrase for the user to say
   * AGC: Builds an example like "Marca Jacto, Horímetro 500, Ano 2020, Largura 14, Tanque 3000"
   */
  const getExamplePhrase = useCallback(() => {
    const fields = getMissingFields()
    const examples: string[] = []
    for (const field of fields) {
      if (field.name === 'brand') examples.push(t('voice.exampleBrand'))
      else if (field.name === 'horimeterHours') examples.push('Horímetro 500')
      else if (field.name === 'year') examples.push('Ano 2020')
      else if (field.name === 'width') examples.push('Largura 14')
      else if (field.name === 'tankCapacity') examples.push('Tanque 3000')
      else if (field.name === 'title') examples.push(t('voice.exampleTaskTitle'))
      else if (field.name === 'category') examples.push(t('voice.exampleCategory'))
      else if (field.name === 'field') examples.push('Talhão 35B')
      else if (field.name === 'dueDate') examples.push(t('voice.exampleDueDate'))
      else if (field.name === 'assignee') examples.push(t('voice.exampleAssignee'))
      else if (field.name === 'quantity') examples.push('Valor 15000 reais')
      else if (field.name === 'priority') continue
      else if (field.name === 'machinery') continue
      else examples.push(`${field.label} ...`)
    }
    return examples.join(', ')
  }, [getMissingFields])

  /**
   * AGC: Get supported MIME type for MediaRecorder
   */
  const getSupportedMimeType = useCallback((): string => {
    const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) return type
    }
    return 'audio/webm'
  }, [])

  /**
   * AGC: Start recording in the pre-modal for dictating field values
   */
  const startPreModalRecording = useCallback(async () => {
    try {
      console.log('🎤 AGC: Starting pre-modal recording for field values...')
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      // AGC: v3.9.41 — Cache stream for v2 so "Corrigir" can re-record without a new getUserMedia.
      if (AGENT_V2_ENABLED) v2StreamRef.current = stream
      const mimeType = getSupportedMimeType()
      const recorder = new MediaRecorder(stream, { mimeType })
      
      preModalChunksRef.current = []
      preModalRecorderRef.current = recorder

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          preModalChunksRef.current.push(event.data)
        }
      }

      recorder.start(250) // AGC: Collect data every 250ms
      setPreModalRecording(true)
      setPreModalDuration(0)

      // AGC: Start duration timer
      preModalTimerRef.current = setInterval(() => {
        setPreModalDuration(prev => prev + 1)
      }, 1000)

      console.log('✅ AGC: Pre-modal recording started')
    } catch (err) {
      console.error('❌ AGC: Pre-modal recording error:', err)
      if (isMediaPermissionError(err)) {
        setShowMicBlocked(true)
      } else {
        toast.error(t('voice.micPermissionError', 'Erro ao acessar microfone'))
      }
    }
  }, [getSupportedMimeType, t])

  /**
   * AGC: Stop pre-modal recording, send to extract-fields endpoint, merge results
   */
  const stopPreModalRecording = useCallback(async () => {
    console.log('🎤 AGC: Stopping pre-modal recording...')

    // AGC: Stop timer
    if (preModalTimerRef.current) {
      clearInterval(preModalTimerRef.current)
      preModalTimerRef.current = null
    }

    const recorder = preModalRecorderRef.current
    if (!recorder || recorder.state === 'inactive') {
      setPreModalRecording(false)
      return
    }

    setPreModalRecording(false)
    setPreModalProcessing(true)

    // AGC: Wait for recorder to fully stop and provide final data
    await new Promise<void>((resolve) => {
      recorder.onstop = async () => {
        try {
          // AGC: Use recorder's own mimeType — same fix as main recording path (2.13.12).
          // AGC: Calling getSupportedMimeType() again can return a different value and mislabels
          // AGC: the blob container, causing Whisper to reject it as "Invalid file format".
          const recorderMime = recorder.mimeType || getSupportedMimeType()
          const blob = new Blob(preModalChunksRef.current, { type: recorderMime })
          console.log(`📤 AGC: Pre-modal audio blob: ${(blob.size / 1024).toFixed(1)} KB (mime: ${recorderMime})`)

          // AGC: Build expected fields from fieldSuggestions
          const expectedFields = getMissingFields().map(f => ({
            name: f.name,
            label: f.label,
            type: f.type,
          }))

          // AGC: Determine vehicle type from parsedData
          const vehicleType = (lastResponse?.parsedData?.type as string) || 
                              (lastResponse?.parsedData?.category as string) || ''

          // AGC: Upload to extract-fields endpoint
          const backendUrl = getBackendUrl()
          const formData = new FormData()
          // AGC: Derive extension from blob type — same logic as uploadAudio() in useVoiceRecording.ts
          const baseType = ((blob.type || '').split(';')[0] || '').trim().toLowerCase()
          let ext = 'webm'
          if (baseType.includes('mp4') || baseType.includes('m4a') || baseType.includes('aac')) ext = 'mp4'
          else if (baseType.includes('ogg')) ext = 'ogg'
          else if (baseType.includes('wav')) ext = 'wav'
          formData.append('audio', blob, `field-recording.${ext}`)
          formData.append('expectedFields', JSON.stringify(expectedFields))
          formData.append('vehicleType', vehicleType)
          formData.append('language', 'pt')

          console.log('📤 AGC: Sending to extract-fields endpoint...')
          const response = await fetch(`${backendUrl}/api/v1/voice/extract-fields`, {
            method: 'POST',
            body: formData,
          })

          if (!response.ok) {
            throw new Error(`Extract fields failed: ${response.status}`)
          }

          const result = await response.json()
          console.log('✅ AGC: Extract fields result:', result)

          if (result.success && result.extractedValues) {
            setPreModalTranscription(result.transcription || null)
            
            // AGC: Merge extracted values into inputValues
            const newInputValues: Record<string, string> = {}
            for (const [key, value] of Object.entries(result.extractedValues)) {
              if (value !== null && value !== undefined && value !== '') {
                newInputValues[key] = String(value)
              }
            }

            // AGC: Match voice-extracted field name against registered field suggestions
            // AGC: Voice extraction returns raw text (e.g., "Talão 33", "35B"), need to match to Firestore ID
            if (newInputValues.field && lastResponse?.fieldSuggestions?.field?.suggestions) {
              const voiceFieldRaw = newInputValues.field.toLowerCase().trim()
              // AGC: Strip common prefixes like "talão", "talhão", "lote", "campo" from voice input
              const voiceFieldName = voiceFieldRaw.replace(/^(?:talh[aã]o|lote|campo|field|plot)\s*/i, '').trim()
              const fieldSuggs = lastResponse.fieldSuggestions.field.suggestions as Array<{ value: string; label: string }>
              
              // AGC: Try exact match on label
              const exactMatch = fieldSuggs.find(s => s.label.toLowerCase() === voiceFieldRaw || s.label.toLowerCase() === voiceFieldName)
              if (exactMatch) {
                newInputValues.field = exactMatch.value
                newInputValues.fieldName = exactMatch.label
                console.log(`✅ AGC: Voice field exact match: "${voiceFieldRaw}" → "${exactMatch.label}" (ID: ${exactMatch.value})`)
              } else {
                // AGC: Try partial/fuzzy match:
                // AGC: 1. Label contains voice name or voice name contains label
                // AGC: 2. Plot name (before parenthetical farm name) starts with voice input (e.g., "33" matches "33a")
                const partialMatch = fieldSuggs.find(s => {
                  const labelLower = s.label.toLowerCase()
                  // AGC: Extract plot name without farm label — e.g., "33a (Curitiba)" → "33a"
                  const plotNameOnly = labelLower.replace(/\s*\(.*\)$/, '').trim()
                  return labelLower.includes(voiceFieldName) || 
                    voiceFieldName.includes(plotNameOnly) ||
                    plotNameOnly.startsWith(voiceFieldName) ||
                    voiceFieldName.startsWith(plotNameOnly)
                })
                if (partialMatch) {
                  newInputValues.field = partialMatch.value
                  newInputValues.fieldName = partialMatch.label
                  console.log(`🔍 AGC: Voice field fuzzy match: "${voiceFieldRaw}" → "${partialMatch.label}" (ID: ${partialMatch.value})`)
                } else {
                  // AGC: No match - keep the raw text so user sees it and selects manually
                  console.log(`⚠️ AGC: Voice field "${voiceFieldRaw}" not matched to any registered field`)
                  // AGC: Store original voice value as fieldName for display
                  newInputValues.fieldName = newInputValues.field
                  delete newInputValues.field // AGC: Don't set field ID to raw text
                }
              }
            }

            // AGC: Match voice-extracted assignee self-references to 'self' value
            if (newInputValues.assignee) {
              const SELF_PHRASES = ['self', 'eu', 'eu mesmo', 'eu mesma', 'myself', 'yo', 'yo mismo', 'yo misma', 'me']
              if (SELF_PHRASES.includes(newInputValues.assignee.toLowerCase().trim())) {
                newInputValues.assignee = 'self'
                newInputValues.assigneeName = 'Eu mesmo (padrão)'
                console.log('👤 AGC: Assignee self-reference detected from voice → mapped to "self"')
              }
            }
            
            console.log('📋 AGC: Merging extracted values:', newInputValues)
            setInputValues(prev => ({ ...prev, ...newInputValues }))
            toast.success(t('voice.valuesCaptured', 'Valores capturados!'))
          } else {
            toast.error(t('voice.extractionFailed', 'Não foi possível extrair os valores'))
          }
        } catch (err) {
          console.error('❌ AGC: Extract fields error:', err)
          toast.error(t('voice.extractionError', 'Erro ao processar áudio'))
        }

        setPreModalProcessing(false)
        // AGC: Move to confirmation form phase
        setFieldPhase('confirmFields')
        resolve()
      }

      // AGC: Stop the recorder - triggers onstop
      recorder.stop()
      // AGC: Stop all tracks
      recorder.stream?.getTracks().forEach(track => track.stop())
    })
  }, [getSupportedMimeType, getMissingFields, lastResponse, t])

  /**
   * AGC: Skip pre-modal and go directly to manual field entry
   */
  const skipPreModal = useCallback(() => {
    console.log('⏭️ AGC: Skipping pre-modal, going to manual field entry')
    setFieldPhase('confirmFields')
  }, [])

  // AGC: When agent mode is active and modal becomes visible while hook is recording,
  // AGC: mirror to local pre-modal recorder for the v2 agent flow.
  // AGC: Skip in processingOnly mode — pendingBlob handles that path instead.
  useEffect(() => {
    if (isVisible && !processingOnly && (AGENT_V2_ENABLED || (useAgent && voiceAgentEnabled)) && state === 'recording' && !preModalRecording) {
      setAgentPhase('recording')
      startPreModalRecording()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, useAgent, voiceAgentEnabled, state])

  // AGC: Recording bar flow — when modal opens with a pendingBlob, send it to v2 agent.
  // AGC: This replicates handleStopRecording's v2 path but with a pre-captured blob
  // AGC: instead of stopping a live pre-modal recorder.
  useEffect(() => {
    if (!isVisible || !pendingBlob || pendingBlob.size === 0) return
    setAgentPhase('processing')
    setV2Busy(true)
    onSilentCancel?.()
    sendToAgentV2(pendingBlob, language, { reset: true })
      .then((resp) => {
        sessionIdRef.current = resp.sessionId
        setV2Response(resp)
      })
      .catch((err) => {
        console.error('[voice-v2] pendingBlob send error:', err)
        toast.error(t('voice.agent.errorGeneric'))
      })
      .finally(() => {
        setV2Busy(false)
        setAgentPhase('idle')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, pendingBlob])

  // AGC: IMPORTANT - Early return MUST be after ALL hooks (useState, useEffect, useCallback, useRef)
  // AGC: React requires hooks to be called in the same order every render
  if (!isVisible) return null

  const secondaryMessage = getSecondaryMessage()

  // AGC: Status pills + close button must be rendered OUTSIDE the backdrop-filter div.
  // AGC: backdrop-filter creates a new CSS containing block for position:fixed descendants,
  // AGC: so any child with `fixed` positioning is positioned relative to the modal (centered)
  // AGC: instead of the viewport (top-left). Rendering as siblings bypasses this entirely.
  const statusBarStyle: React.CSSProperties = {
    position: 'fixed',
    top: 'calc(env(safe-area-inset-top) + 1rem)',
    left: '1rem',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  }
  const closeButtonStyle: React.CSSProperties = {
    position: 'fixed',
    top: 'calc(env(safe-area-inset-top) + 1rem)',
    right: '1rem',
    zIndex: 10000,
    padding: '0.5rem',
    color: 'rgba(255,255,255,0.6)',
  }
  // AGC: "Stop and Send" button pinned above the mic area.
  // AGC: IMPORTANT: must sit WELL ABOVE the mic button in BottomNav, otherwise when the user
  // AGC: long-presses the mic and the modal opens, releasing the touch lands on "Parar e Enviar"
  // AGC: at the same pixel — immediately stopping the recording. 14rem clears the nav + mic.
  const stopButtonStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: 'calc(env(safe-area-inset-bottom) + 10rem)',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10000,
  }

  // AGC: v3.9.11 — TTS with native Capacitor plugin on Android (Web Speech is silent on WebView)
  // AGC: Falls back to window.speechSynthesis on desktop/web.
  // AGC: v3.9.15 — Picks first female pt-BR voice once and caches the index. Pitch 0.9 / rate 0.95
  // AGC: for a softer, slightly slower delivery. Voice gender isn't a first-class field in Android TTS,
  // AGC: so we infer from the name (engines commonly include "female", "fem", "f00#", "afs", "br_f").
  // AGC: Refs themselves are declared above the `if (!isVisible) return null` early return.
  const resolvePreferredVoice = async (lang: string): Promise<number | undefined> => {
    if (voiceLookupDoneRef.current) return preferredVoiceIdxRef.current ?? undefined
    voiceLookupDoneRef.current = true
    try {
      const result = await TextToSpeech.getSupportedVoices()
      const voices = (result?.voices ?? []) as Array<{ name: string; lang: string }>
      const langPrefix = lang.split('-')[0]
      const ptVoices = voices
        .map((v, idx) => ({ ...v, idx }))
        .filter(v => v.lang?.toLowerCase().startsWith(langPrefix))
      const femaleHints = /(female|fem\b|_f\b|f00|afs|br_f|woman|joana|camila|francisca|luciana)/i
      const female = ptVoices.find(v => femaleHints.test(v.name || ''))
      const pick = female || ptVoices[0]
      console.log(`🔊 AGC: TTS voices for ${lang} — ${ptVoices.length} found; picked: ${pick?.name || 'default'}`)
      preferredVoiceIdxRef.current = pick?.idx ?? null
      return pick?.idx
    } catch (err) {
      console.error('🔊 AGC: getSupportedVoices failed:', err)
      return undefined
    }
  }

  // AGC: v3.9.17 — Try backend Google TTS (natural Brazilian female) first; fall back to
  // AGC: native Capacitor TTS only if the network call fails. Backend returns MP3, frontend
  // AGC: plays it via HTMLAudioElement with a blob URL.
  const playRemoteTts = async (text: string, lang: string): Promise<boolean> => {
    try {
      const token = await getAuthToken()
      if (!token) return false
      const backendUrl = getBackendUrl()
      const res = await fetch(`${backendUrl}/api/v1/voice/agent-tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text, lang: lang.split('-')[0] }),
      })
      if (!res.ok) {
        console.warn(`🔊 AGC: Remote TTS HTTP ${res.status} — falling back to native`)
        return false
      }
      const blob = await res.blob()
      console.log(`🔊 AGC: Remote TTS got ${(blob.size / 1024).toFixed(1)}KB MP3`)
      const url = URL.createObjectURL(blob)
      await new Promise<void>((resolve, reject) => {
        const audio = new Audio(url)
        audio.onended = () => { URL.revokeObjectURL(url); resolve() }
        audio.onerror = (e) => { URL.revokeObjectURL(url); reject(e) }
        audio.play().catch(reject)
      })
      return true
    } catch (err) {
      console.error('🔊 AGC: Remote TTS failed, falling back to native:', err)
      return false
    }
  }

  const speakText = async (text: string, opts?: { skipAutoReopen?: boolean }) => {
    // AGC: v3.9.14 — coerce to string in case backend/caller sent an object
    const safeText = typeof text === 'string' ? text : JSON.stringify(text ?? '')
    const langMap: Record<string, string> = { en: 'en-US', pt: 'pt-BR', es: 'es-ES' }
    const lang = langMap[language] || 'pt-BR'
    const afterSpeak = () => {
      isAgentSpeakingRef.current = false
      if (opts?.skipAutoReopen) {
        setAgentPhase('idle')
        return
      }
      if (agentMode === 'A') {
        setAgentPhase('recording')
        startPreModalRecording()
      } else {
        setAgentPhase('idle')
        onClose()
      }
    }
    isAgentSpeakingRef.current = true
    setAgentPhase('speaking')

    // AGC: v3.9.17 — Try Google Cloud TTS first (natural Brazilian female).
    const remoteOk = await playRemoteTts(safeText, lang)
    if (remoteOk) {
      afterSpeak()
      return
    }

    if (Capacitor.isNativePlatform()) {
      try {
        const voice = await resolvePreferredVoice(lang)
        console.log(`🔊 AGC: Native TTS speaking (${lang}, voice=${voice ?? 'default'}): "${safeText.slice(0, 60)}"`)
        await TextToSpeech.speak({
          text: safeText,
          lang,
          rate: 0.95,
          pitch: 0.9,
          volume: 1.0,
          category: 'ambient',
          ...(voice !== undefined ? { voice } : {}),
        })
      } catch (err) {
        console.error('🔊 AGC: Native TTS failed:', err)
      }
      afterSpeak()
      return
    }
    if (!('speechSynthesis' in window)) { afterSpeak(); return }
    window.speechSynthesis.cancel()
    const utter = new SpeechSynthesisUtterance(safeText)
    utter.lang = lang
    utter.rate = 0.95
    utter.pitch = 0.9
    // AGC: Web Speech voice selection — pick first female pt-BR voice if available
    const allVoices = window.speechSynthesis.getVoices()
    const femaleHints = /(female|fem\b|joana|camila|francisca|luciana)/i
    const langPrefix = lang.split('-')[0]
    const female = allVoices.find(v => v.lang?.toLowerCase().startsWith(langPrefix) && femaleHints.test(v.name))
    if (female) utter.voice = female
    utter.onstart = () => { isAgentSpeakingRef.current = true }
    utter.onend = afterSpeak
    window.speechSynthesis.speak(utter)
  }

  // AGC: v3.9.4 — Farm Agent voice-chat upload
  const sendToAgentChat = async (audioBlob: Blob) => {
    try {
      console.log(`📤 AGC: sendToAgentChat — blob ${(audioBlob.size / 1024).toFixed(1)}KB sessionId=${sessionIdRef.current ?? 'NEW'}`)
      const formData = new FormData()
      formData.append('audio', audioBlob, 'recording.webm')
      formData.append('lang', language)
      formData.append('mode', agentMode)
      if (sessionIdRef.current) {
        formData.append('sessionId', sessionIdRef.current)
      } else {
        // AGC: v3.9.21 — first turn after modal open: ask backend to reset the persistent
        // AGC: voice:${uid} session so prior denials/empty turns don't anchor the LLM.
        formData.append('reset', '1')
      }

      // AGC: Auth — same pattern as VoiceContext: get Firebase ID token and pass as Bearer header
      const token = await getAuthToken()
      const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

      const backendUrl = getBackendUrl()
      const res = await fetch(`${backendUrl}/api/v1/voice/agent-chat`, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
      })
      console.log(`📥 AGC: agent-chat response status=${res.status}`)

      if (res.status === 402) {
        toast.error(t('voice.agent.errorGeneric'))
        setUseAgent(false)
        setAgentPhase('idle')
        return
      }
      if (res.status === 429) {
        toast.error(t('voice.capReached.voiceApp'))
        setAgentPhase('idle')
        return
      }
      if (!res.ok) {
        toast.error(t('voice.agent.errorGeneric'))
        setAgentPhase('idle')
        return
      }

      const data = await res.json()
      const replyText = typeof data.replyText === 'string'
        ? data.replyText
        : (data.replyText?.message || data.replyText?.text || JSON.stringify(data.replyText ?? ''))
      console.log(`📥 AGC: agent-chat reply (${replyText.length}ch, empty=${!!data.empty}): "${replyText.slice(0, 100)}"`)
      sessionIdRef.current = data.sessionId
      setTranscript(prev => [...prev, { role: 'agent', text: replyText }])
      // AGC: v3.9.19 — when backend signals empty transcription, speak the message but DON'T
      // AGC: auto-reopen the mic in Mode A. Otherwise we loop forever ("didn't hear" → restart → "didn't hear").
      if (data.empty) {
        await speakText(replyText, { skipAutoReopen: true })
        setAgentPhase('idle')
      } else {
        await speakText(replyText)
      }
    } catch (err) {
      console.error('[voice/agent-chat] error:', err)
      toast.error(t('voice.agent.errorGeneric'))
      setAgentPhase('idle')
    }
  }

  return (
    <>
      {/* AGC: Status bar - siblings to the backdrop-filter div so `position:fixed` resolves to the viewport */}
      <div style={statusBarStyle}>
        {/* AGC: Voice mode indicator */}
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
          voiceMode === 'A'
            ? 'bg-yellow-500/20 text-yellow-300'
            : 'bg-emerald-500/20 text-emerald-300'
        }`}>
          {voiceMode === 'A' ? (
            <>
              <Cloud className="w-3.5 h-3.5" />
              <span>Whisper</span>
            </>
          ) : (
            <>
              <Smartphone className="w-3.5 h-3.5" />
              <span>Nativo</span>
            </>
          )}
        </div>

        {/* AGC: Online/Offline indicator */}
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
          isOnline
            ? 'bg-green-500/20 text-green-300'
            : 'bg-red-500/20 text-red-300'
        }`}>
          {isOnline ? (
            <>
              <Wifi className="w-3.5 h-3.5" />
              <span>Online</span>
            </>
          ) : (
            <>
              <WifiOff className="w-3.5 h-3.5" />
              <span>Offline</span>
            </>
          )}
        </div>

        {/* AGC: Queue count indicator (only show if there are queued commands) */}
        {queueCount > 0 && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-orange-500/20 text-orange-300">
            <span>Fila: {queueCount}</span>
          </div>
        )}
      </div>

      {/* AGC: Close button - sibling to backdrop-filter div, fixed to viewport top-right */}
      <button
        onClick={state === 'recording' ? onCancelRecording : onClose}
        style={closeButtonStyle}
        aria-label="Fechar"
      >
        <X className="w-8 h-8" />
      </button>

      {/* AGC: "Parar e Enviar" button - pinned to viewport bottom, always visible during recording */}
      {/* AGC: Sibling outside the backdrop-filter div so it's never trapped in the scroll container */}
      {/* AGC: v3.9.4 — In agent mode, handleStopRecording intercepts to send blob to agent-chat */}
      {/* AGC: v3.9.9 — In Chat mode both `state==='recording'` (hook) and `preModalRecording`
          (mirror recorder) are true at the same time. Without the agent-mode guard on the pre-modal
          button, both render stacked and the second one wins z-order, routing taps to
          stopPreModalRecording (extract-fields) instead of handleStopRecording (agent-chat). */}
      {/* AGC: v3.9.20 — unified state-machine button so the user has ONE thing to look at:
          GREEN "Parar e Enviar" (tappable, recording) → ORANGE "Pensando…" (disabled, agent processing)
          → ORANGE "Falando…" (disabled, TTS playing) → GREEN again (recording turn 2). */}
      {AGENT_V2_ENABLED || (useAgent && voiceAgentEnabled) ? (
        !v2Response && ((!processingOnly && (state === 'recording' || preModalRecording)) || agentPhase === 'processing' || agentPhase === 'speaking') && (
          <button
            onClick={() => {
              if (agentPhase === 'processing' || agentPhase === 'speaking') return
              console.log('👆 AGC: Parar e Enviar (agent) tapped')
              handleStopRecording()
            }}
            disabled={agentPhase === 'processing' || agentPhase === 'speaking'}
            style={{
              ...stopButtonStyle,
              ...(agentPhase === 'processing' || agentPhase === 'speaking' ? { backgroundColor: '#46B179', boxShadow: '0 10px 15px -3px rgba(70,177,121,0.3)' } : {}),
            }}
            className={`px-8 py-3 rounded-full font-semibold text-base shadow-xl active:scale-95 transition-all flex items-center gap-2 ${
              agentPhase === 'processing'
                ? 'text-white cursor-not-allowed'
                : agentPhase === 'speaking'
                  ? 'text-white cursor-not-allowed'
                  : 'bg-emerald-500 text-white shadow-emerald-500/30'
            }`}
          >
            {(agentPhase === 'processing' || agentPhase === 'speaking') && (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            {agentPhase === 'processing' ? 'Pensando...' : agentPhase === 'speaking' ? 'Falando...' : 'Parar e Enviar'}
          </button>
        )
      ) : (
        !processingOnly && state === 'recording' && (
          <button
            onClick={() => { console.log('👆 AGC: Parar e Enviar (intent) tapped'); handleStopRecording() }}
            style={stopButtonStyle}
            className="px-8 py-3 bg-emerald-500 text-white rounded-full font-semibold text-base shadow-xl shadow-emerald-500/30 active:scale-95 transition-transform"
          >
            Parar e Enviar
          </button>
        )
      )}
      {/* AGC: Pre-modal recording also shows bottom "Parar e Enviar" — state stays 'idle' during
          pre-modal so the main button above never fires; this one targets stopPreModalRecording.
          Excluded in agent/Chat mode where handleStopRecording owns the flow. */}
      {!processingOnly && preModalRecording && !AGENT_V2_ENABLED && !(useAgent && voiceAgentEnabled) && (
        <button
          onClick={stopPreModalRecording}
          style={stopButtonStyle}
          className="px-8 py-3 bg-emerald-500 text-white rounded-full font-semibold text-base shadow-xl shadow-emerald-500/30 active:scale-95 transition-transform"
        >
          Parar e Enviar
        </button>
      )}

    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex flex-col items-center justify-center backdrop-blur-sm overflow-y-auto"
      data-testid="voice-recording-modal"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >

      {/* AGC: Main content */}
      <div className="flex flex-col items-center py-8">

        {/* AGC: v3.9.4 — PREMIUM Chat/Intent segmented control — hidden when v2 is active */}
        {voiceAgentEnabled && !AGENT_V2_ENABLED && (
          <div className="flex items-center gap-2 text-sm mb-2">
            <button
              type="button"
              onClick={() => { if (CHAT_MODE_FUNCTIONAL) setUseAgent(true) }}
              disabled={!CHAT_MODE_FUNCTIONAL}
              title={!CHAT_MODE_FUNCTIONAL ? 'Em breve' : undefined}
              className={
                !CHAT_MODE_FUNCTIONAL
                  ? 'bg-gray-500/30 text-white/40 px-3 py-1 rounded cursor-not-allowed line-through decoration-white/40'
                  : useAgent
                    ? 'bg-green-600 text-white px-3 py-1 rounded'
                    : 'bg-gray-200 px-3 py-1 rounded'
              }
              aria-pressed={useAgent}
            >
              {t('voice.agent.chatMode')}{!CHAT_MODE_FUNCTIONAL ? ' (em breve)' : ''}
            </button>
            <button
              type="button"
              onClick={() => setUseAgent(false)}
              className={!useAgent ? 'bg-green-600 text-white px-3 py-1 rounded' : 'bg-gray-200 px-3 py-1 rounded'}
              aria-pressed={!useAgent}
            >
              {t('voice.agent.intentMode')}
            </button>
            {useAgent && (
              <div className="ml-2 flex items-center gap-1">
                <button type="button" onClick={() => setAgentMode('A')} className={agentMode === 'A' ? 'underline font-semibold text-white' : 'text-white/60'}>{t('voice.agent.modeA')}</button>
                <button type="button" onClick={() => setAgentMode('B')} className={agentMode === 'B' ? 'underline font-semibold text-white' : 'text-white/60'}>{t('voice.agent.modeB')}</button>
              </div>
            )}
          </div>
        )}

        {/* AGC: v3.9.24 — Voice Agent v2 confirmation box; rendered before v1 transcript */}
        {AGENT_V2_ENABLED && v2Response && (
          <VoiceAgentConfirm
            response={v2Response}
            busy={v2Busy}
            onConfirm={async () => {
              setV2Busy(true)
              try {
                const resp = await confirmAgentAction('confirm', language)
                setV2Response(resp)
              } finally { setV2Busy(false) }
            }}
            onReject={async () => {
              setV2Busy(true)
              try {
                const resp = await confirmAgentAction('reject', language)
                setV2Response(resp)
              } finally { setV2Busy(false) }
            }}
            onEdit={async () => {
              // AGC: v3.9.43 — Reuse v2StreamRef (kept alive from first recording) instead
              // AGC: of calling getUserMedia again — Android WebView returns a muted track
              // AGC: on subsequent calls.  Fallback to new getUserMedia if stream is dead.
              setV2Response(null)
              setAgentPhase('recording')
              try {
                let stream = v2StreamRef.current
                const trackAlive = stream?.getAudioTracks().some(t => t.readyState === 'live')
                if (!stream || !trackAlive) {
                  stream = await navigator.mediaDevices.getUserMedia({ audio: true })
                  v2StreamRef.current = stream
                }
                const mimeType = getSupportedMimeType()
                const recorder = new MediaRecorder(stream, { mimeType })
                preModalChunksRef.current = []
                preModalRecorderRef.current = recorder
                recorder.ondataavailable = (event) => {
                  if (event.data.size > 0) preModalChunksRef.current.push(event.data)
                }
                recorder.start(250)
                setPreModalRecording(true)
                setPreModalDuration(0)
                preModalTimerRef.current = setInterval(() => {
                  setPreModalDuration(prev => prev + 1)
                }, 1000)
              } catch (err) {
                console.error('AGC: [voice-v2] edit-restart failed:', err)
                setAgentPhase('idle')
                toast.error(t('voice.agent.errorGeneric'))
              }
            }}
            onSendText={async (text: string) => {
              setV2Busy(true)
              setAgentPhase('processing')
              try {
                const resp = await sendTextToAgentV2(text, language)
                sessionIdRef.current = resp.sessionId
                setV2Response(resp)
              } catch (err) {
                console.error('[voice-v2] text send error:', err)
                toast.error(t('voice.agent.errorGeneric'))
              } finally {
                setV2Busy(false)
                setAgentPhase('idle')
              }
            }}
            onClose={() => {
              // AGC: v3.9.43 — Release kept-alive stream on close
              if (v2StreamRef.current) {
                v2StreamRef.current.getTracks().forEach(t => t.stop())
                v2StreamRef.current = null
              }
              setV2Response(null)
              onClose()
            }}
          />
        )}

        {/* AGC: v3.9.4 — Agent transcript area — only visible in agent mode with messages */}
        {/* AGC: v3.9.11 — larger, higher-contrast bubbles so user sees the reply even if TTS fails */}
        {useAgent && transcript.length > 0 && (
          <div className="max-h-72 overflow-y-auto mb-3 space-y-2 px-2 w-full max-w-md">
            {transcript.map((msg, i) => (
              <div key={i} className={msg.role === 'user' ? 'text-right' : 'text-left'}>
                <div className={msg.role === 'user' ? 'bg-blue-400/30 text-white inline-block px-4 py-2 rounded-lg text-base' : 'bg-emerald-500/25 text-white inline-block px-4 py-3 rounded-lg text-base font-medium border border-emerald-400/30'}>
                  {msg.text}
                </div>
                {msg.role === 'agent' && (
                  <button
                    type="button"
                    onClick={() => speakText(msg.text)}
                    className="ml-1 text-xs text-blue-400"
                    aria-label={t('voice.agent.replay')}
                  >🔊</button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* AGC: Farm Agent thinking UI — shown during processing/speaking phase */}
        {AGENT_V2_ENABLED && !v2Response && (agentPhase === 'processing' || agentPhase === 'speaking' || v2Busy) && (
          <div className="flex flex-col items-center justify-center flex-1">
            <img src="/droid-pensando.gif" alt="Processing" className="w-96 h-96 object-contain" style={{ transform: 'translateX(5px)' }} />
          </div>
        )}

        {/* AGC: processingOnly fallback — only for non-v2 mode (v2 uses its own processing gif above) */}
        {processingOnly && !AGENT_V2_ENABLED && (state === 'idle' || state === 'recording') && (
          <div className="flex flex-col items-center justify-center flex-1">
            <img src="/droid-pensando.gif" alt="Processing" className="w-96 h-96 object-contain" style={{ transform: 'translateX(5px)' }} />
          </div>
        )}

        {/* AGC: Progress ring + status text — HIDDEN when wizard confirmFields, v2 confirm, or processingOnly is active */}
        {!processingOnly && fieldPhase !== 'confirmFields' && !(AGENT_V2_ENABLED && v2Response) && !(AGENT_V2_ENABLED && (agentPhase === 'processing' || v2Busy)) && (
          <>
            {/* AGC: Progress ring with icon */}
            <div className="relative">
              <svg
                width={size}
                height={size}
                className="transform -rotate-90"
                data-testid="progress-ring"
              >
                {/* AGC: Background circle */}
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth={strokeWidth}
                />
                {/* AGC: Progress circle */}
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={getRingColor()}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-100"
                  data-testid="progress-circle"
                />
              </svg>

              {/* AGC: Icon centered in ring */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center">
                  {renderIcon()}
                </div>
              </div>
            </div>

            {/* AGC: Duration display */}
            {state === 'recording' && (
              <p className="mt-4 text-white text-3xl font-mono">
                {formatDuration(duration)}
              </p>
            )}

            {/* AGC: Status message */}
            <p className="mt-4 text-white text-lg font-medium text-center px-8">
              {getStatusMessage()}
            </p>

            {/* AGC: Secondary message for errors */}
            {secondaryMessage && (
              <p className="mt-2 text-white/60 text-sm text-center px-8">
                {secondaryMessage}
              </p>
            )}

            {/* AGC: Transcription display */}
            {lastResponse?.transcription && state !== 'recording' && (
              <div className="mt-4 max-w-sm px-6">
                <p className="text-white/60 text-sm text-center italic">
                  Você disse: "{lastResponse.transcription}"
                </p>
              </div>
            )}
          </>
        )}

        {/* AGC: [SOTA] Risk flags display — show warnings/info from SOTA engine */}
        {lastResponse?.riskFlags && lastResponse.riskFlags.length > 0 && state === 'idle' && (
          <div className="mt-2 max-w-sm px-6 space-y-1">
            {lastResponse.riskFlags.map((rf: { flag: string; severity: string; message: string }, idx: number) => (
              <div
                key={`risk-${idx}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${
                  rf.severity === 'blocking'
                    ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                    : rf.severity === 'warning'
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                    : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                }`}
              >
                <span>{rf.severity === 'blocking' ? '🚫' : rf.severity === 'warning' ? '⚠️' : 'ℹ️'}</span>
                <span>{rf.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* AGC: Farm selection UI - shown when user has multiple farms and needs to select one */}
        {lastResponse?.requiresFarmSelection && lastResponse?.farms && lastResponse.farms.length > 0 && state === 'idle' && (
          <div className="mt-4 w-full max-w-sm px-4">
            <div className="bg-gradient-to-b from-emerald-500/15 to-green-500/10 border border-emerald-500/30 rounded-xl p-4">
              {/* AGC: Title */}
              <h3 className="text-white text-base font-semibold mb-3 text-center flex items-center justify-center gap-2">
                🏠 {t('voice.selectFarm', 'Selecione a Fazenda')}
              </h3>
              
              <p className="text-white/70 text-xs text-center mb-3">
                {t('voice.selectFarmDescription', 'Em qual fazenda deseja criar o talhão?')}
              </p>

              {/* AGC: Farm list */}
              <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                {lastResponse.farms.map((farm: FarmSelectionInfo) => (
                  <button
                    key={farm.id}
                    onClick={() => {
                      setSelectedFarmId(farm.id)
                      setShowNewFarmInput(false)
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-colors ${
                      selectedFarmId === farm.id
                        ? 'bg-emerald-500/30 border border-emerald-500/50 text-emerald-200'
                        : 'bg-white/5 hover:bg-white/10 border border-transparent text-white/80'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {selectedFarmId === farm.id && <Check className="w-4 h-4 text-emerald-400" />}
                      <div>
                        <p className="text-sm font-medium">{farm.name}</p>
                        {farm.lotCount > 0 && (
                          <p className="text-xs text-white/50">
                            {farm.lotCount} {farm.lotCount === 1 ? t('voice.lot') : t('voice.lots')}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
                
                {/* AGC: Create new farm option */}
                <button
                  onClick={() => {
                    setShowNewFarmInput(true)
                    setSelectedFarmId(null)
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    showNewFarmInput
                      ? 'bg-blue-500/30 border border-blue-500/50 text-blue-200'
                      : 'bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 text-blue-300'
                  }`}
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm">{t('voice.createNewFarm', 'Criar nova fazenda')}</span>
                </button>
              </div>
              
              {/* AGC: New farm name input */}
              {showNewFarmInput && (
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder={t('voice.newFarmName', 'Nome da nova fazenda...')}
                    value={newFarmName}
                    onChange={(e) => setNewFarmName(e.target.value)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-white/40 focus:outline-none focus:border-blue-500"
                    autoFocus
                  />
                </div>
              )}

              {/* AGC: Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (showNewFarmInput && newFarmName.trim() && onCreateFarm) {
                      // AGC: Create new farm and use it for the lot
                      onCreateFarm(newFarmName.trim(), lastResponse.pendingLotData || {})
                      onClose()
                    } else if (selectedFarmId && onFarmSelect) {
                      // AGC: Use selected farm for the lot
                      const selectedFarm = lastResponse.farms?.find(f => f.id === selectedFarmId)
                      if (selectedFarm) {
                        onFarmSelect(selectedFarmId, selectedFarm.name, lastResponse.pendingLotData || {})
                        onClose()
                      }
                    } else {
                      toast.error(t('voice.selectFarmError', 'Selecione uma fazenda'))
                    }
                  }}
                  disabled={!selectedFarmId && (!showNewFarmInput || !newFarmName.trim())}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    (selectedFarmId || (showNewFarmInput && newFarmName.trim()))
                      ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                      : 'bg-white/10 text-white/40 cursor-not-allowed'
                  }`}
                >
                  <Check className="w-4 h-4" />
                  {showNewFarmInput ? t('voice.createAndContinue', 'Criar e Continuar') : t('common.confirm', 'Confirmar')}
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white/80 font-medium transition-colors"
                >
                  {t('common.cancel', 'Cancelar')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AGC: PRE-MODAL PHASE - Instructs user to dictate missing field values */}
        {lastResponse?.fieldSuggestions && Object.keys(lastResponse.fieldSuggestions).length > 0 && state === 'idle' && fieldPhase === 'preModal' && (
          <div className="mt-4 w-full max-w-sm px-4">
            <div className="bg-gradient-to-b from-blue-500/15 to-indigo-500/10 border border-blue-500/30 rounded-xl p-4">
              {/* AGC: Title */}
              <h3 className="text-white text-base font-semibold mb-3 text-center">
                {t('voice.preModalTitle', 'Informações Necessárias')}
              </h3>

              {/* AGC: Detected info summary - shows entry type and what was understood */}
              {lastResponse?.parsedData && lastResponse?.entryType && (
                <div className="mb-3 p-2.5 bg-emerald-500/10 border border-emerald-500/25 rounded-lg">
                  <p className="text-emerald-300 text-xs font-medium mb-1">
                    {getEntryTypeLabel(lastResponse.entryType)}
                  </p>
                  {/* AGC: Show friendly summary of what was detected - title/name and category if available */}
                  <div className="flex flex-wrap gap-1.5">
                    {(lastResponse.parsedData.title || lastResponse.parsedData.name) && (
                      <span className="px-2 py-0.5 bg-emerald-500/15 rounded text-white text-xs">
                        {lastResponse.parsedData.title || lastResponse.parsedData.name}
                      </span>
                    )}
                    {lastResponse.parsedData.category && lastResponse.parsedData.category !== 'outros' && (
                      <span className="px-2 py-0.5 bg-emerald-500/15 rounded text-white/80 text-xs">
                        {lastResponse.parsedData.category}
                      </span>
                    )}
                    {lastResponse.parsedData.dueDate && (
                      <span className="px-2 py-0.5 bg-emerald-500/15 rounded text-white/80 text-xs">
                        {lastResponse.parsedData.dueDate}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* AGC: Missing fields list */}
              <div className="mb-3">
                <p className="text-white/70 text-xs font-medium mb-2">
                  {t('voice.pleaseInform', 'Por favor informe')}:
                </p>
                <div className="space-y-1.5">
                  {getMissingFields().map((field) => (
                    <div key={field.name} className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg">
                      <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                      <span className="text-white/80 text-sm">{field.label}</span>
                      <span className="text-white/30 text-xs ml-auto">
                        {field.type === 'number' ? '(número)' : field.isBrand ? '(nome)' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* AGC: Example phrase */}
              <div className="mb-4 p-2.5 bg-white/5 border border-white/10 rounded-lg">
                <p className="text-white/50 text-xs mb-1">{t('voice.exampleSay', 'Exemplo - Diga')}:</p>
                <p className="text-blue-300 text-sm italic">
                  &quot;{getExamplePhrase()}&quot;
                </p>
              </div>

              {/* AGC: Pre-modal microphone button */}
              {preModalProcessing ? (
                <div className="flex flex-col items-center gap-2 mb-3">
                  <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
                  </div>
                  <p className="text-blue-300 text-xs">{t('voice.processing', 'Processando...')}</p>
                </div>
              ) : preModalRecording ? (
                <div className="flex flex-col items-center gap-2 mb-3">
                  <button
                    onClick={stopPreModalRecording}
                    className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors animate-pulse shadow-lg shadow-red-500/30"
                  >
                    <Square className="w-6 h-6 text-white fill-white" />
                  </button>
                  <p className="text-red-300 text-xs font-mono">
                    {String(Math.floor(preModalDuration / 60)).padStart(2, '0')}:{String(preModalDuration % 60).padStart(2, '0')}
                  </p>
                  <p className="text-white/50 text-xs">{t('voice.tapToStop', 'Toque para parar')}</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 mb-3">
                  <button
                    onClick={startPreModalRecording}
                    className="w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center transition-colors shadow-lg shadow-emerald-500/20"
                  >
                    <Mic className="w-8 h-8 text-white" />
                  </button>
                  <p className="text-white/50 text-xs">{t('voice.tapToRecord', 'Toque para gravar')}</p>
                </div>
              )}

              {/* AGC: Pre-modal transcription result */}
              {preModalTranscription && (
                <div className="mb-3 p-2 bg-white/5 rounded-lg">
                  <p className="text-white/40 text-xs">{t('voice.youSaid', 'Você disse')}:</p>
                  <p className="text-white/80 text-sm italic">&quot;{preModalTranscription}&quot;</p>
                </div>
              )}

              {/* AGC: Skip button - go directly to manual input */}
              <button
                onClick={skipPreModal}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white/60 hover:text-white/80 text-sm transition-colors"
              >
                {t('voice.skipToManual', 'Preencher manualmente')}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* AGC: Field suggestions popup - shown when values weren't recognized (CONFIRM FIELDS PHASE) */}
        {lastResponse?.fieldSuggestions && Object.keys(lastResponse.fieldSuggestions).length > 0 && state === 'idle' && fieldPhase === 'confirmFields' && (() => {
          return null
        })()}
        {lastResponse?.fieldSuggestions && Object.keys(lastResponse.fieldSuggestions).length > 0 && state === 'idle' && fieldPhase === 'confirmFields' && (
          <div className="mt-2 w-full max-w-md px-3 max-h-[85vh] overflow-y-auto">
            {confirmMode === 'wizard' ? (
              <VoiceWizardConfirmation
                lastResponse={lastResponse}
                inputValues={inputValues}
                setInputValues={setInputValues}
                onConfirmFields={onConfirmFields}
                onFieldSuggestionSelect={onFieldSuggestionSelect}
                onClose={onClose}
                onModeSwitch={() => handleModeSwitch('single')}
                onPermissionBlocked={() => setShowMicBlocked(true)}
                farms={farms}
                selectedFarmId={selectedFarmId}
                setSelectedFarmId={setSelectedFarmId}
                stockToggle={stockToggle}
                setStockToggle={setStockToggle}
                customFinancialCategories={customFinancialCategories}
                onAddCustomCategory={handleAddCustomCategory}
              />
            ) : (
              <VoiceSinglePageConfirmation
                lastResponse={lastResponse}
                inputValues={inputValues}
                setInputValues={setInputValues}
                onConfirmFields={onConfirmFields}
                onFieldSuggestionSelect={onFieldSuggestionSelect}
                onClose={onClose}
                onModeSwitch={() => handleModeSwitch('wizard')}
                onPermissionBlocked={() => setShowMicBlocked(true)}
                farms={farms}
                selectedFarmId={selectedFarmId}
                setSelectedFarmId={setSelectedFarmId}
                stockToggle={stockToggle}
                setStockToggle={setStockToggle}
                customFinancialCategories={customFinancialCategories}
                onAddCustomCategory={handleAddCustomCategory}
              />
            )}
          </div>
        )}

        {/* AGC: Entry type badge (only show on success) */}
        {lastResponse?.success && lastResponse?.entryType && state !== 'recording' && (
          <div className="mt-4">
            <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 text-sm rounded-full">
              {getEntryTypeLabel(lastResponse.entryType)}
            </span>
          </div>
        )}

        {/* AGC: Manual entry options - shown when command not recognized */}
        {showManualOptions && state === 'idle' && (
          <div className="mt-6 w-full max-w-sm px-6">
            <p className="text-white/80 text-sm font-medium text-center mb-4">
              Ou crie manualmente:
            </p>
            <div className="grid grid-cols-2 gap-3">
              {ENTRY_TYPE_IDS.map((type) => (
                <button
                  key={type.id}
                  onClick={() => handleManualEntrySelect(type.id)}
                  className="flex items-center gap-2 px-4 py-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-left"
                >
                  <span className="text-lg">{type.icon}</span>
                  <span className="text-white text-sm">{t(type.labelKey)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* AGC: Action buttons */}
        {/* AGC: "Parar e Enviar" moved to fixed viewport-bottom sibling (outside backdrop-filter div) */}
        <div className="mt-8 flex gap-4">
          {state === 'error' && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-2 px-6 py-3 bg-white/20 text-white rounded-full font-medium hover:bg-white/30 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Tentar Novamente
            </button>
          )}

          {/* AGC: Show retry button when command not recognized */}
          {showManualOptions && state === 'idle' && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-500/20 text-emerald-300 rounded-full font-medium hover:bg-emerald-500/30 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Tentar Novamente
            </button>
          )}
        </div>

        {/* AGC: Hint text */}
        {!processingOnly && state === 'recording' && (
          <p className="mt-6 text-white/40 text-sm">
            Solte o botão ou toque em "Parar e Enviar"
          </p>
        )}
      </div>

      {/* AGC: Microphone permission blocked modal */}
      <PermissionBlockedModal
        isOpen={showMicBlocked}
        permissionType="microphone"
        onClose={() => setShowMicBlocked(false)}
      />
    </div>
    </>
  )
}

/**
 * AGC: Get human-readable label for entry type
 */
function getEntryTypeLabel(entryType: string): string {
  const labels: Record<string, string> = {
    farm: '🏠 Fazenda',
    task: '📋 Tarefa',
    financial: '💰 Financeiro',
    expense: '💰 Despesa',
    revenue: '💵 Receita',
    field: '🗺️ Talhão',
    inventory: '📦 Estoque',
    vehicle: '🚜 Veículo',
  }
  return labels[entryType] || '📝 Registro'
}

export default VoiceRecordingModal
